import { useTranslation } from 'react-i18next';

interface Props {
  correct: boolean;
  partiallyCorrect?: boolean;
  feedback: string;
  correctAnswer: string;
  xpEarned: number;
  levelUp?: string;
  onNext: () => void;
}

export default function FeedbackScreen({ correct, partiallyCorrect, feedback, correctAnswer, xpEarned, levelUp, onNext }: Props) {
  const { t } = useTranslation();
  const bgColor = correct ? 'bg-green-50' : partiallyCorrect ? 'bg-amber-50' : 'bg-red-50';
  const borderColor = correct ? 'border-green-200' : partiallyCorrect ? 'border-amber-200' : 'border-red-200';
  const textColor = correct ? 'text-green-700' : partiallyCorrect ? 'text-amber-700' : 'text-red-700';
  const emoji = correct ? '🎉' : partiallyCorrect ? '🟡' : '❌';
  const title = correct ? t('learning.correct') : partiallyCorrect ? t('learning.almostCorrect') : t('learning.incorrectTitle');

  return (
    <div className="space-y-4">
      {levelUp && (
        <div className="text-center bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl p-4 animate-bounce">
          <p className="text-2xl">🎊</p>
          <p className="text-lg font-bold">{t('learning.levelUp')}</p>
          <p className="text-sm opacity-90">{t('learning.reachedLevel', { level: levelUp })}</p>
        </div>
      )}

      <div className={`${bgColor} border-2 ${borderColor} rounded-xl p-5`}>
        <div className="text-center mb-3">
          <span className="text-3xl">{emoji}</span>
          <h3 className={`text-xl font-bold ${textColor} mt-1`}>{title}</h3>
          <p className={`text-sm ${textColor} opacity-75`}>+{xpEarned} XP</p>
        </div>

        {!correct && (
          <div className="bg-white rounded-lg p-3 mb-3">
            <p className="text-xs text-gray-400 mb-1">{t('learning.correctAnswer')}</p>
            <p className="text-gray-800 font-medium">{correctAnswer}</p>
          </div>
        )}

        {feedback && !correct && (
          <div className="bg-white rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-1">{t('learning.explanation')}</p>
            <p className="text-gray-700 text-sm">{feedback}</p>
          </div>
        )}
      </div>

      <button
        onClick={onNext}
        className="w-full py-4 bg-indigo-600 text-white font-bold rounded-xl text-lg hover:bg-indigo-700 active:scale-98 transition"
        autoFocus
      >
        {t('learning.next')}
      </button>
    </div>
  );
}
